import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Col, Progress, Row, Spin, Statistic, Table, Tag } from 'antd';
import {
  CarOutlined,
  CheckCircleOutlined,
  ContainerOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFetchData } from '../../hooks';
import { PageableResponse, TrackingRequestRow } from '../../types';
import { BillOfMaterialList } from '../../types/BillOfMaterial';
import { convertDate, dateFormat } from '../../lib/utils';

const MAROON = 'rgb(162, 50, 49)';

// Same colours as the tracker page's stage tags, so a stage means the same
// colour in both places.
const STAGE_COLORS: Record<TrackingRequestRow['stage'], string> = {
  RAISED: '#8c8c8c',
  CANVASSED: '#d48806',
  ORDERED: '#1677ff',
  RECEIVED: '#08979c',
  DELIVERED: '#389e0d',
};

const STAGE_ORDER: TrackingRequestRow['stage'][] = [
  'RAISED',
  'CANVASSED',
  'ORDERED',
  'RECEIVED',
  'DELIVERED',
];

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  CONFIRM: 'processing',
  APPROVED: 'success',
  READY: 'cyan',
  DONE: 'green',
  CANCEL: 'error',
};

const Dashboard = () => {
  // One header-only fetch of BOMs — everything below is derived from it.
  const { data: bomData, isLoading } = useFetchData<
    PageableResponse<BillOfMaterialList>
  >('/production/boms', {
    resource: 'min',
    length: 100000,
    active: true,
    status: 'DRAFT,CONFIRM,APPROVED,READY,DONE,CANCEL',
    from: '2000-01-01',
    to: '2999-12-31',
  });

  // Cheap count-only fetches (recordsTotal) for the other modules. The MR/RR
  // list endpoints default to a date window that matches nothing, so the
  // counts must ask for the full range explicitly -- without it both cards
  // showed 0 against real counts of 106 and 27. /production/orders has no
  // date window, so it needs none.
  const { data: poData } = useFetchData<PageableResponse<unknown>>(
    '/production/orders',
    { length: 1, active: true },
  );
  const { data: mrData } = useFetchData<PageableResponse<unknown>>(
    '/stock/mrs',
    {
      length: 1,
      active: true,
      from: '2000-01-01',
      to: '2999-12-31',
    },
  );
  const { data: rrData } = useFetchData<PageableResponse<unknown>>(
    '/stock/rrs',
    {
      length: 1,
      active: true,
      from: '2000-01-01',
      to: '2999-12-31',
    },
  );

  // The request tracker: one fetch, everything below derived from it — the
  // same pattern as the BOM stats above, and the same endpoint the tracker
  // page reads, so the two can never disagree.
  const { data: trackingData } =
    useFetchData<TrackingRequestRow[]>('/tracking/requests');

  const trackingStats = useMemo(() => {
    const rows = trackingData ?? [];
    const byStage: Record<string, number> = {};
    rows.forEach((r) => {
      byStage[r.stage] = (byStage[r.stage] || 0) + 1;
    });
    // Delivered requests are done, not waiting, so they are out of the list.
    const stale = rows
      .filter((r) => r.stage !== 'DELIVERED' && (r.daysInStage ?? 0) >= 7)
      .sort((a, b) => (b.daysInStage ?? 0) - (a.daysInStage ?? 0));
    return { total: rows.length, byStage, stale };
  }, [trackingData]);

  const stats = useMemo(() => {
    const rows = bomData?.data ?? [];
    const byStatus: Record<string, number> = {};
    rows.forEach((r) => {
      const s = (r.status as unknown as string) || 'UNKNOWN';
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    const today = dayjs().startOf('day');
    const in90 = today.add(90, 'day');
    const upcoming = rows
      .filter((r) => {
        if (!r.dateshipment) return false;
        const d = dayjs(r.dateshipment);
        return d.isValid() && !d.isBefore(today) && !d.isAfter(in90);
      })
      .sort(
        (a, b) =>
          dayjs(a.dateshipment).valueOf() - dayjs(b.dateshipment).valueOf(),
      );
    return { total: rows.length, byStatus, upcoming };
  }, [bomData]);

  const kpis = [
    {
      title: 'Total BOMs',
      value: stats.total,
      icon: <FileTextOutlined />,
      color: MAROON,
      to: '/production/bill-of-material',
    },
    {
      title: 'Confirmed',
      value: stats.byStatus['CONFIRM'] || 0,
      icon: <CheckCircleOutlined />,
      color: '#1677ff',
    },
    {
      title: 'Approved',
      value: stats.byStatus['APPROVED'] || 0,
      icon: <FileDoneOutlined />,
      color: '#389e0d',
    },
    {
      title: 'Shipping in 90 days',
      value: stats.upcoming.length,
      icon: <CarOutlined />,
      color: '#d48806',
    },
    {
      title: 'Production Orders',
      value: poData?.recordsTotal ?? 0,
      icon: <ContainerOutlined />,
      color: '#531dab',
      // No destination: /production/orders is served by the API and counted
      // here, but the module has no page and no menu entry. The card linked to
      // /production/production-order, which is not a registered route, so every
      // click landed on the catch-all 404. It stays a figure until the module
      // exists, like Confirmed and Approved above.
    },
    {
      title: 'Material Requests',
      value: mrData?.recordsTotal ?? 0,
      icon: <InboxOutlined />,
      color: '#08979c',
      to: '/stock-movement/material-request',
    },
    {
      title: 'Receiving Reports',
      value: rrData?.recordsTotal ?? 0,
      icon: <InboxOutlined />,
      color: '#c41d7f',
      to: '/stock-movement/receiving-report',
    },
  ];

  const statusOrder = [
    'CONFIRM',
    'APPROVED',
    'READY',
    'DONE',
    'DRAFT',
    'CANCEL',
  ];
  const maxStatus = Math.max(1, ...Object.values(stats.byStatus));

  const upcomingColumns = [
    {
      title: 'BOM',
      dataIndex: 'code',
      render: (code: string, r: BillOfMaterialList) => (
        <Link to={`/production/bill-of-material/${r.id}`}>{code}</Link>
      ),
    },
    { title: 'Buyer', dataIndex: 'buyercode' },
    {
      title: 'Shipment Date',
      dataIndex: 'dateshipment',
      render: (d: string) => (d ? convertDate(d, dateFormat) : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) => (
        <Tag color={STATUS_COLORS[s] || 'default'} bordered={false}>
          {s}
        </Tag>
      ),
    },
  ];

  const staleColumns = [
    {
      title: 'PR',
      dataIndex: 'code',
      render: (code: string, r: TrackingRequestRow) => (
        <Link to={`/purchase-request/${r.id}`}>{code}</Link>
      ),
    },
    { title: 'Requested By', dataIndex: 'requestedBy' },
    {
      title: 'Stage',
      dataIndex: 'stage',
      render: (s: TrackingRequestRow['stage']) => (
        <Tag color={STAGE_COLORS[s]} bordered={false}>
          {s}
        </Tag>
      ),
    },
    {
      title: 'Days in Stage',
      dataIndex: 'daysInStage',
      render: (v: number | null) => (
        <span style={{ color: '#cf1322' }}>{v ?? '—'}</span>
      ),
    },
  ];

  const cardShadow = { boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#1f2937' }}
        >
          Production Dashboard
        </h1>
        <span style={{ color: '#6b7280' }}>
          Bill of Material &amp; materials overview ·{' '}
          {dayjs().format('DD MMM YYYY')}
        </span>
      </div>

      <Spin spinning={isLoading}>
        <Row gutter={[16, 16]}>
          {kpis.map((k) => {
            const card = (
              <Card bordered={false} style={cardShadow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      color: '#fff',
                      background: k.color,
                    }}
                  >
                    {k.icon}
                  </div>
                  <Statistic title={k.title} value={k.value} />
                </div>
              </Card>
            );
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={k.title}>
                {k.to ? <Link to={k.to}>{card}</Link> : card}
              </Col>
            );
          })}
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={10}>
            <Card
              title="BOMs by Status"
              bordered={false}
              style={{ ...cardShadow, height: '100%' }}
            >
              {statusOrder
                .filter((s) => stats.byStatus[s])
                .map((s) => (
                  <div key={s} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 2,
                      }}
                    >
                      <span>{s}</span>
                      <strong>{stats.byStatus[s]}</strong>
                    </div>
                    <Progress
                      percent={Math.round(
                        (stats.byStatus[s] / maxStatus) * 100,
                      )}
                      showInfo={false}
                      strokeColor={MAROON}
                    />
                  </div>
                ))}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title="Upcoming Shipments (next 90 days)"
              bordered={false}
              style={{ ...cardShadow, height: '100%' }}
              extra={<Link to="/production/bill-of-material">View all</Link>}
            >
              <Table<BillOfMaterialList>
                rowKey="id"
                size="small"
                columns={upcomingColumns}
                dataSource={stats.upcoming}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                locale={{ emptyText: 'No shipments in the next 90 days' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={10}>
            <Card
              title={`Request Pipeline (${trackingStats.total})`}
              bordered={false}
              style={{ ...cardShadow, height: '100%' }}
            >
              {STAGE_ORDER.filter((s) => trackingStats.byStage[s]).map((s) => (
                <div key={s} style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                    }}
                  >
                    <span>{s}</span>
                    <strong>{trackingStats.byStage[s]}</strong>
                  </div>
                  <Progress
                    percent={Math.round(
                      (trackingStats.byStage[s] /
                        Math.max(1, trackingStats.total)) *
                        100,
                    )}
                    showInfo={false}
                    strokeColor={STAGE_COLORS[s]}
                  />
                </div>
              ))}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title="Requests Waiting (7+ days in stage)"
              bordered={false}
              style={{ ...cardShadow, height: '100%' }}
              extra={<Link to="/tracking">View all</Link>}
            >
              <Table<TrackingRequestRow>
                rowKey="id"
                size="small"
                columns={staleColumns}
                dataSource={trackingStats.stale}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                locale={{ emptyText: 'No requests waiting 7+ days' }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Dashboard;
